import { intlLocale, t } from '../i18n';

const dtf = (o: Intl.DateTimeFormatOptions) => new Intl.DateTimeFormat(intlLocale(), o);

export function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return dtf({ day: '2-digit', month: '2-digit', year: 'numeric' }).format(d);
}

export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return dtf({ day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }).format(d);
}

export function formatTime(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return dtf({ hour: '2-digit', minute: '2-digit' }).format(d);
}

/** Тривалість у хвилинах → "5 хв", "2 год 10 хв", "3 д". */
export function formatDuration(minutes: number): string {
  const m = Math.max(0, Math.round(Math.abs(minutes)));
  if (m < 60) return t('time.min', { n: m });
  if (m < 60 * 24) {
    const h = Math.floor(m / 60);
    const r = m % 60;
    return r ? t('time.hourMin', { h, m: r }) : t('time.hour', { n: h });
  }
  return t('time.day', { n: Math.floor(m / 1440) });
}

/** "щойно", "5 хв тому", "сьогодні 14:20", "вчора", або дата. */
export function formatRelative(iso: string | null | undefined, now: Date = new Date()): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const diffMin = (now.getTime() - d.getTime()) / 60000;
  if (diffMin >= 0 && diffMin < 1) return t('time.justNow');
  if (diffMin >= 0 && diffMin < 60) return t('time.ago', { t: formatDuration(diffMin) });
  const sameDay = d.toDateString() === now.toDateString();
  if (sameDay) return `${t('time.today')} ${formatTime(iso)}`;
  const y = new Date(now);
  y.setDate(now.getDate() - 1);
  if (d.toDateString() === y.toDateString()) return t('time.yesterday');
  return formatDate(iso);
}

export type SlaState = { kind: 'none' } | { kind: 'met'; minutes: number } | { kind: 'left'; minutes: number } | { kind: 'overdue'; minutes: number };

/** Стан SLA першої відповіді для ліда. */
export function slaState(
  dueAt: string | null | undefined,
  firstResponseAt: string | null | undefined,
  createdAt: string | null | undefined,
  now: Date = new Date(),
): SlaState {
  if (firstResponseAt) {
    const c = createdAt ? new Date(createdAt).getTime() : NaN;
    const r = new Date(firstResponseAt).getTime();
    return { kind: 'met', minutes: Number.isNaN(c) ? 0 : (r - c) / 60000 };
  }
  if (!dueAt) return { kind: 'none' };
  const diff = (new Date(dueAt).getTime() - now.getTime()) / 60000;
  return diff >= 0 ? { kind: 'left', minutes: diff } : { kind: 'overdue', minutes: -diff };
}

/** ISO → значення для <input type="datetime-local">. */
export function toLocalInput(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

/** <input type="datetime-local"> → ISO з таймзоною. */
export function fromLocalInput(v: string): string | undefined {
  if (!v) return undefined;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? undefined : d.toISOString();
}

export function isoDay(d: Date): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}
